/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useParams } from "react-router-dom";
import { Box, Spacer, Text } from "@nordcloud/gnui";
import { FlexContainer, QueryPagination, Table } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import { ManageRoles } from "~/components/Role/ManageRoles";
import { useUserConsumer } from "~/context/User/User";
import { RouteParams } from "~/models";
import { isStandardUser } from "~/routing";
import { useAxios } from "~/tools";
import { AmazonConnectDetails } from "~/views/customer/teamMembers/AmazonConnectDetails";
import { AssignTeamMember } from "./AssignTeamMember";
import { TeamMembersListColumns } from "./TeamMembersList";

function TeamMembers() {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [team, setTeam] = React.useState([]);
  const { customerId } = useParams<RouteParams>();

  const { role } = useUserConsumer();
  const canEdit = isStandardUser(role);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchTeam = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/customers/${customerId}/employees`);
      setTeam(data?.employees);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, customerId, showError]);

  React.useEffect(() => {
    customerId !== "" && fetchTeam();
  }, [fetchTeam, customerId]);

  const columns = React.useMemo(
    () => TeamMembersListColumns(customerId, fetchTeam, canEdit),
    [customerId, fetchTeam, canEdit]
  );

  return (
    <>
      <AmazonConnectDetails />

      <Box>
        <FlexContainer>
          <Text weight="medium">Team members</Text>
          <div css={{ flexGrow: 1 }} />

          <ManageRoles fetchTeam={fetchTeam} />
          <AssignTeamMember
            setIsSidebarOpen={setIsSidebarOpen}
            isSidebarOpen={isSidebarOpen}
            refetch={fetchTeam}
            team={team}
          />
        </FlexContainer>
        <Spacer height="1rem" />

        <LoaderWrap loading={isLoading}>
          <Table columns={columns} data={team || []} />
          {team.length > 0 && <QueryPagination count={team.length} />}
        </LoaderWrap>
      </Box>
    </>
  );
}

export { TeamMembers };
